import React from 'react';

/**
 * SkeletonCard — animated skeleton placeholder for loading states.
 * Props:
 *   lines   {number}  — number of text lines (default 3)
 *   hasAvatar {boolean} — show avatar circle on left
 *   hasImage  {boolean} — show image block at top
 *   dark      {boolean} — dark background variant
 *   className {string}  — extra classes for wrapper
 */
const SkeletonCard = ({ lines = 3, hasAvatar = false, hasImage = false, dark = false, className = '' }) => {
  const shimmer = dark ? 'bg-white/10 after:bg-gradient-to-r after:from-transparent after:via-white/5 after:to-transparent' : 'bg-[#0a1628]/8 after:bg-gradient-to-r after:from-transparent after:via-white/60 after:to-transparent';
  const base = `relative overflow-hidden rounded-lg ${shimmer} animate-pulse`;

  return (
    <div className={`rounded-2xl p-4 ${dark ? 'bg-[#0d1f3c] border border-white/10' : 'bg-white border border-[#0a1628]/8'} ${className}`}>
      {hasImage && (
        <div className={`w-full h-32 rounded-xl mb-4 ${dark ? 'bg-white/10' : 'bg-[#0a1628]/8'} animate-pulse`} />
      )}
      <div className="flex items-start gap-3">
        {hasAvatar && (
          <div className={`w-10 h-10 rounded-full flex-shrink-0 ${dark ? 'bg-white/10' : 'bg-[#0a1628]/10'} animate-pulse`} />
        )}
        <div className="flex-1 space-y-2.5">
          {Array.from({ length: lines })?.map((_, i) => (
            <div
              key={i}
              className={`h-3 rounded-full ${dark ? 'bg-white/10' : 'bg-[#0a1628]/8'} animate-pulse`}
              style={{ width: i === 0 ? '70%' : i === lines - 1 ? '45%' : '90%' }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * SkeletonList — renders N skeleton cards.
 * Props:
 *   count   {number}
 *   dark    {boolean}
 *   hasAvatar {boolean}
 */
export const SkeletonList = ({ count = 3, dark = false, hasAvatar = false }) => (
  <div className="space-y-3">
    {Array.from({ length: count })?.map((_, i) => (
      <SkeletonCard key={i} dark={dark} hasAvatar={hasAvatar} lines={3} />
    ))}
  </div>
);

/**
 * SkeletonRow — single-line skeleton row (for table/list items).
 */
export const SkeletonRow = ({ dark = false, cols = 3 }) => (
  <div className={`flex items-center gap-3 p-4 rounded-xl ${dark ? 'bg-white/5' : 'bg-[#0a1628]/4'} animate-pulse`}>
    {Array.from({ length: cols })?.map((_, i) => (
      <div
        key={i}
        className={`h-3 rounded-full ${dark ? 'bg-white/10' : 'bg-[#0a1628]/10'}`}
        style={{ flex: i === 0 ? '2' : '1' }}
      />
    ))}
  </div>
);

/**
 * SkeletonStatCard — skeleton for stat/metric cards.
 */
export const SkeletonStatCard = ({ dark = false }) => (
  <div className={`rounded-2xl p-4 ${dark ? 'bg-[#0d1f3c] border border-white/10' : 'bg-white border border-[#0a1628]/8'} animate-pulse`}>
    <div className={`h-3 w-16 rounded-full mb-3 ${dark ? 'bg-white/10' : 'bg-[#0a1628]/10'}`} />
    <div className={`h-7 w-24 rounded-lg mb-2 ${dark ? 'bg-white/15' : 'bg-[#0a1628]/12'}`} />
    <div className={`h-2.5 w-20 rounded-full ${dark ? 'bg-white/8' : 'bg-[#0a1628]/6'}`} />
  </div>
);

export default SkeletonCard;
